import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { get_orders } from "../../store/reducers/orderReducer";
import CurrencyFormat from "../shared/CurrencyFormat";
import Pagination2 from "../Pagination2";
import Filter from "../Filter";

const StatusBadge = ({ status, type }) => {
  const getStatusConfig = () => {
    if (type === "payment") {
      switch (status) {
        case "paid":
          return {
            backgroundColor: "bg-green-300",
            textColor: "text-green-800",
            text: "Pagado",
          };
        case "pending":
          return {
            backgroundColor: "bg-yellow-300",
            textColor: "text-yellow-800",
            text: "Pendiente",
          };
        case "cancelled":
          return {
            backgroundColor: "bg-red-300",
            textColor: "text-red-800",
            text: "Cancelado",
          };
        default:
          return {
            backgroundColor: "bg-gray-300",
            textColor: "text-gray-800",
            text: "Desconocido",
          };
      }
    }

    // Delivery status
    switch (status) {
      case "completed":
        return {
          backgroundColor: "bg-green-300",
          textColor: "text-green-800",
          text: "Entregado",
        };
      case "processing":
        return {
          backgroundColor: "bg-blue-300",
          textColor: "text-blue-800",
          text: "Lista",
        };
      case "preparing":
        return {
          backgroundColor: "bg-yellow-300",
          textColor: "text-yellow-800",
          text: "Preparando",
        };
      case "new order":
        return {
          backgroundColor: "bg-purple-300",
          textColor: "text-purple-800",
          text: "Orden Nueva",
        };
      case "cancelled":
        return {
          backgroundColor: "bg-red-300",
          textColor: "text-red-800",
          text: "Cancelada",
        };
      default:
        return {
          backgroundColor: "bg-gray-300",
          textColor: "text-gray-800",
          text: "Estado Desconocido",
        };
    }
  };

  const { backgroundColor, textColor, text } = getStatusConfig();

  return (
    <span
      className={`px-3 py-1 rounded-full text-sm font-medium ${backgroundColor} ${textColor}`}
    >
      {text}
    </span>
  );
};

const Orders = () => {
  const [state, setState] = useState("all");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const { myOrders, totalOrders } = useSelector((state) => state.order);

  const [currentPage, setCurrentPage] = useState(1);
  const [parPage, setParPage] = useState(5);

  useEffect(() => {
    const obj = {
      parPage: parseInt(parPage),
      page: parseInt(currentPage),
      status: state,
      customerId: userInfo.id,
    };
    dispatch(get_orders(obj));
  }, [state, dispatch, userInfo.id, currentPage, parPage]);

  const redirect = (ord) => {
    let items = 0;
    for (let i = 0; i < ord.length; i++) {
      items = ord.products[i].quantity + items;
    }
    navigate("/payment", {
      state: {
        price: ord.price,
        items,
        orderId: ord._id,
      },
    });
  };

  // Card view for mobile
  const OrderCard = ({ order, numeration }) => (
    <div className="bg-white rounded-lg shadow p-4 mb-4">
      <div className="flex justify-between items-start mb-3">
        <div className="text-sm font-medium text-gray-500">
          No {numeration + 1}
        </div>
        <div className="text-sm font-medium">
          <CurrencyFormat value={order.price} />
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-500">Estado del Pago:</span>
          <StatusBadge status={order.payment_status} type="payment" />
        </div>

        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-500">Estado de Entrega:</span>
          <StatusBadge status={order.delivery_status} type="delivery" />
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <Link to={`/dashboard/order/details/${order._id}`}>
          <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1.5 rounded">
            Ver
          </span>
        </Link>
        {order.delivery_status !== "cancelled" &&
          order.delivery_status !== "paid" && (
            <span
              onClick={() => redirect(order)}
              className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1.5 rounded cursor-pointer"
            >
              Voucher
            </span>
          )}
      </div>
    </div>
  );

  return (
    <div className="bg-white p-4 md:p-6 rounded-md">
      <div className="mb-3">
        <h2 className="text-xl font-semibold text-slate-600">Mis Ordenes</h2>
      </div>
      <div className="flex justify-between items-center mb-5">
        <Filter setParPage={setParPage} setCurrentPage={setCurrentPage} />
        <select
          className="outline-none px-4 py-2 border rounded-md text-slate-600 cursor-pointer"
          value={state}
          onChange={(e) => setState(e.target.value)}
        >
          <option value="all">--Estado de la Orden--</option>
          <option value="completed">Entregado</option>
          <option value="processing">Lista para Entregar</option>
          <option value="preparing">Preparando Orden</option>
          <option value="new order">Orden Nueva</option>
          <option value="cancelled">Cancelada</option>
        </select>
      </div>

      <div className="lg:hidden">
        {myOrders.map((order, index) => (
          <OrderCard key={index} order={order} numeration={index} />
        ))}
      </div>

      <div className="hidden lg:block">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className="overflow-hidden border rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      No
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Precio
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Estado Pago
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Estado Entrega
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Acción
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {myOrders.map((o, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-500">
                        #{i + 1 + parPage * (currentPage - 1)}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                        <CurrencyFormat value={o.price} />
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                        <StatusBadge status={o.payment_status} type="payment" />
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                        <StatusBadge
                          status={o.delivery_status}
                          type="delivery"
                        />
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-2">
                          <Link to={`/dashboard/order/details/${o._id}`}>
                            <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                              Ver
                            </span>
                          </Link>
                          {o.delivery_status !== "cancelled" && (
                            <span
                              onClick={() => redirect(o)}
                              className="bg-green-100 text-green-800 text-xs font-semibold px-2.5 py-0.5 rounded cursor-pointer"
                            >
                              Voucher
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination2
              pageNumber={currentPage}
              setPageNumber={setCurrentPage}
              totalItem={totalOrders}
              parPage={parPage}
              showItem={3}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Orders;
